import { LoadingIndicator, BusyContent, Button } from '../ui/Primitives';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './WorkflowManager.css';
import { logApiError } from '../../utils/logger';
import { getBackendHost } from '../../utils/backendHost';
import IntegrationProviderIcon from '../IntegrationProviderIcon';
import WorkflowAttention from './WorkflowAttention';

const BUILTIN_META = {
  message: { color: 'message', editLabel: 'Edit message taking' },
  appointment: { color: 'appointment', editLabel: 'Edit appointment booking' },
  transfer: { color: 'routing', editLabel: 'Edit call routing' },
};

const EMPTY_MAPPING = { sourceField: '', sourceFields: [], targetField: '', transform: { type: 'direct' } };
const DEFAULT_ACTIVE_CUSTOM_WORKFLOW_LIMIT = 10;
const WORKFLOW_PREVIEW_COUNT = 6;
const DEFAULT_WORKFLOW_CLOSING = 'Thank the caller, say you will pass their details along to the team, and let them know someone will get back to them. Do not promise a specific time.';
const LEGACY_WORKFLOW_CLOSING = 'Thank the caller and confirm that their information was received.';

function needsFollowUpClosingDraft(workflow) {
  return workflow.workflowType === 'custom' && (!workflow.behavior?.successMessage || workflow.behavior.successMessage === LEGACY_WORKFLOW_CLOSING);
}

function workflowStatusLabel(status) {
  return status === 'active' ? 'Enabled' : status === 'paused' ? 'Paused' : 'Draft';
}

function workflowLibrary(workflows, providers, search, filter, expanded) {
  const enabled = workflow => workflow.publishedStatus === 'active';
  const terms = search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  // Preserve the API's order within each group, including while drafts autosave.
  const ordered = [...workflows.filter(enabled), ...workflows.filter(workflow => !enabled(workflow))];
  const matches = ordered.filter(workflow => {
    if (filter === 'enabled' && !enabled(workflow)) return false;
    const provider = providers.find(item => item.provider === workflow.destination?.provider);
    const text = [workflow.name, workflow.intent?.description, workflow.destination?.provider, provider?.displayName]
      .filter(Boolean).join(' ').toLocaleLowerCase();
    return terms.every(term => text.includes(term));
  });
  return {
    enabledCount: workflows.filter(enabled).length,
    matches,
    visible: terms.length || expanded ? matches : matches.slice(0, WORKFLOW_PREVIEW_COUNT),
  };
}

function ProviderBadge({ provider }) {
  return <IntegrationProviderIcon provider={provider} className="workflow-provider-badge" />;
}

function BusyLabel({ children }) {
  return <LoadingIndicator label={children} />;
}

function lastPublishedLabel(value) {
  if (!value) return 'Not published yet';
  const publishedAt = new Date(value);
  if (Number.isNaN(publishedAt.getTime())) return 'Published';
  return `Last published ${new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(publishedAt)}`;
}

function BuiltinIcon({ type, color }) {
  const shapes = {
    message: <><path d="M5 5h14v10H9l-4 4V5Z" /><path d="M8 9h8M8 12h5" /></>,
    appointment: <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16M8 14h3" /></>,
    transfer: <><path d="M6 5h8a4 4 0 0 1 4 4v10" /><path d="m14 15 4 4 4-4" /><path d="M9 19H6a4 4 0 0 1-4-4V9" /></>,
  };
  return <span className={`workflow-icon ${color}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{shapes[type] || shapes.message}</svg></span>;
}

export function toDraft(workflow) {
  return {
    id: workflow.id,
    workflowType: workflow.workflowType || 'custom',
    outcomeType: workflow.outcomeType || 'crm_record',
    name: workflow.name || '',
    status: workflow.status || 'draft',
    publishedStatus: workflow.publishedStatus || workflow.status || 'draft',
    activatedAt: workflow.activatedAt || null,
    hasUnpublishedChanges: Boolean(workflow.hasUnpublishedChanges),
    intent: {
      description: workflow.intent?.description || '',
      examples: (workflow.intent?.examples || []).join('\n'),
      exclusions: (workflow.intent?.exclusions || []).join('\n'),
      clarificationPrompt: workflow.intent?.clarificationPrompt || '',
    },
    collection: {
      steps: workflow.collection?.steps || [],
    },
    destination: {
      provider: workflow.destination?.provider || '',
      connectionId: workflow.destination?.connectionId || '',
      targetObject: workflow.destination?.targetObject || '',
      context: {
        workspaceId: workflow.destination?.context?.workspaceId || '',
        projectId: workflow.destination?.context?.projectId || '',
        teamId: workflow.destination?.context?.teamId || '',
        assigneeId: workflow.destination?.context?.assigneeId || '',
      },
      sourceFields: workflow.destination?.sourceFields || [],
      fieldMappings: workflow.destination?.fieldMappings?.length
        ? workflow.destination.fieldMappings
        : [{ ...EMPTY_MAPPING }],
      defaults: workflow.destination?.defaults || {},
      ownerPolicy: workflow.destination?.ownerPolicy || 'default_crm_owner',
      mappingId: workflow.destination?.mappingId || '',
      mappingVersion: workflow.destination?.mappingVersion || null,
      legacyMappingId: workflow.destination?.legacyMappingId || '',
      previousDestinations: workflow.destination?.previousDestinations || [],
    },
    behavior: {
      leadIn: workflow.behavior?.leadIn || '',
      successMessage: needsFollowUpClosingDraft(workflow) ? DEFAULT_WORKFLOW_CLOSING : (workflow.behavior?.successMessage || DEFAULT_WORKFLOW_CLOSING),
      failureMessage: workflow.behavior?.failureMessage || 'Take a message and let the caller know the team will follow up.',
    },
  };
}

export function workflowPayload(draft) {
  return {
    name: draft.name,
    intent: {
      ...draft.intent,
      examples: draft.intent.examples.split('\n').map(item => item.trim()).filter(Boolean),
      exclusions: draft.intent.exclusions.split('\n').map(item => item.trim()).filter(Boolean),
    },
    collection: draft.collection,
    destination: {
      ...draft.destination,
      fieldMappings: draft.destination.fieldMappings.filter(row => row.sourceField && row.targetField),
    },
    behavior: draft.behavior,
  };
}

function mappingSourceLabel(mapping, sourceFields) {
  return (mapping.sourceFields || [mapping.sourceField])
    .map(sourceName => sourceFields.find(field => field.apiName === sourceName)?.label || sourceName)
    .filter(Boolean)
    .join(' + ');
}

function CollectionEditor({ steps, sourceFields, freeformLabels = false, onChange, onAdd }) {
  const [expandedQuestions, setExpandedQuestions] = useState([]);
  const availableFields = sourceFields.filter(field => !['callSessionId', 'notes'].includes(field.apiName));
  const updateStep = (index, changes) => onChange(steps.map((step, stepIndex) => (
    stepIndex === index ? { ...step, ...changes } : step
  )));
  const moveStep = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    const nextSteps = [...steps];
    [nextSteps[index], nextSteps[nextIndex]] = [nextSteps[nextIndex], nextSteps[index]];
    onChange(nextSteps);
  };

  return (
    <>
      {steps.length ? (
        <div className="workflow-question-list">
          {steps.map((step, index) => (
            <div className={`workflow-question${expandedQuestions.includes(step.id || `${step.sourceField}-${index}`) ? ' mobile-expanded' : ''}`} key={step.id || `${step.sourceField}-${index}`}>
              <button type="button" className="workflow-question-summary" aria-expanded={expandedQuestions.includes(step.id || `${step.sourceField}-${index}`)} onClick={() => {
                const key = step.id || `${step.sourceField}-${index}`;
                setExpandedQuestions(current => current.includes(key) ? current.filter(id => id !== key) : [...current, key]);
              }}>
                <span className="workflow-question-summary-number">{index + 1}</span>
                <span className="workflow-question-summary-copy"><strong>{step.label || 'Intake question'}</strong><small>{step.spokenPrompt || 'Add the question to ask'}</small><span>{step.required !== false ? 'Required' : 'Optional'}{step.confirm ? ' · Confirm back' : ''}</span></span>
                <span className="workflow-question-summary-action" aria-hidden="true">{expandedQuestions.includes(step.id || `${step.sourceField}-${index}`) ? '−' : '+'}</span>
              </button>
              <span className="workflow-question-order">{index + 1}</span>
              <label>Information
                {freeformLabels ? (
                  <input value={step.label || ''} onChange={event => updateStep(index, { label: event.target.value })} placeholder="Example: Best callback number" />
                ) : (
                  <select value={step.sourceField} onChange={event => {
                    const field = sourceFields.find(item => item.apiName === event.target.value);
                    updateStep(index, { sourceField: event.target.value, label: field?.label || event.target.value });
                  }}>
                    {availableFields.map(field => <option key={field.apiName} value={field.apiName}>{field.label}</option>)}
                  </select>
                )}
              </label>
              <label className="wide">Natural phone question
                <textarea rows={2} value={step.spokenPrompt || ''} onChange={event => updateStep(index, { spokenPrompt: event.target.value })} placeholder="How should the receptionist ask?" />
              </label>
              <div className="workflow-question-flags">
                <label className="workflow-check"><input type="checkbox" checked={step.required !== false} onChange={event => updateStep(index, { required: event.target.checked })} />Required</label>
                <label className="workflow-check"><input type="checkbox" checked={Boolean(step.confirm)} onChange={event => updateStep(index, { confirm: event.target.checked })} />Confirm back</label>
              </div>
              <div className="workflow-question-controls">
                <button type="button" onClick={() => moveStep(index, -1)} disabled={index === 0} aria-label="Move question up">↑</button>
                <button type="button" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1} aria-label="Move question down">↓</button>
                <button type="button" onClick={() => onChange(steps.filter((_, stepIndex) => stepIndex !== index))} aria-label="Remove question">×</button>
              </div>
            </div>
          ))}
        </div>
      ) : <div className="workflow-question-empty">No extra questions yet. The receptionist will still complete the service’s standard steps.</div>}
      <button type="button" className="workflow-link-button" onClick={onAdd}>+ Add intake question</button>
    </>
  );
}

function WorkflowStatusToggle({ status, publishedStatus, hasUnpublishedChanges, busy, disabled = false, inactiveMessage = '', onChange }) {
  const active = status === 'active';
  const statusPending = hasUnpublishedChanges && status !== publishedStatus;
  return (
    <label className={`workflow-status-toggle ${active ? 'active' : 'paused'} ${busy ? 'busy' : ''} ${disabled ? 'disabled' : ''}`}>
      <span><strong>{busy ? <LoadingIndicator label="Saving workflow status…" /> : statusPending ? `${workflowStatusLabel(status)} after publish` : workflowStatusLabel(status)}</strong><small>{busy ? null : statusPending ? `Callers still use the ${workflowStatusLabel(publishedStatus).toLowerCase()} version` : active ? 'Available to callers' : inactiveMessage || 'Not used during calls'}</small></span>
      <input type="checkbox" role="switch" checked={active} onChange={onChange} disabled={busy || disabled} aria-label={active ? 'Pause workflow after publishing' : 'Enable workflow after publishing'} />
      <i aria-hidden="true" />
    </label>
  );
}

function WorkflowManager({ orgMember, activeBuiltin, onSelectBuiltin, onCustomEditorChange, onDraftStateChange, publishedVersion, refreshVersion, setupGoal, onBusyChange }) {
  const [workflows, setWorkflows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [draft, setDraft] = useState(null);
  const [objects, setObjects] = useState([]);
  const [objectPermissionMessage, setObjectPermissionMessage] = useState('');
  const [destinationOptions, setDestinationOptions] = useState({
    workspaces: [], projects: [], teams: [], assignees: [],
  });
  const [metadata, setMetadata] = useState(null);
  const [questionRecommendation, setQuestionRecommendation] = useState(null);
  const [selectedQuestionRecommendations, setSelectedQuestionRecommendations] = useState([]);
  const [mappingRecommendation, setMappingRecommendation] = useState(null);
  const [selectedMappingRecommendations, setSelectedMappingRecommendations] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
  const [activeCustomWorkflowLimit, setActiveCustomWorkflowLimit] = useState(null);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [libraryExpanded, setLibraryExpanded] = useState(false);
  const searchRef = useRef(null);
  const editRevision = useRef(0);

  const backendHost = getBackendHost();
  const jwtToken = localStorage.getItem('jwt_authorization');
  const authConfig = useMemo(() => ({ headers: { Authorization: `Bearer ${jwtToken}` } }), [jwtToken]);
  const isAdmin = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);
  const customWorkflows = workflows.filter(workflow => workflow.workflowType === 'custom');
  const library = useMemo(() => workflowLibrary(
    setupGoal ? workflows.filter(workflow => setupGoal === 'crm' ? workflow.workflowType === 'custom' : workflow.outcomeType === setupGoal) : workflows, providers, librarySearch, libraryFilter, libraryExpanded,
  ), [workflows, providers, librarySearch, libraryFilter, libraryExpanded, setupGoal]);
  const activeCustomWorkflowCount = customWorkflows.filter(workflow => workflow.status === 'active').length;
  const activeCustomWorkflowLimitReached = Boolean(
    draft?.workflowType === 'custom'
    && draft.status !== 'active'
    && activeCustomWorkflowLimit !== null
    && activeCustomWorkflowCount >= activeCustomWorkflowLimit
  );
  const selectedProvider = providers.find(provider => provider.provider === draft?.destination.provider);
  const providerStatus = statuses[draft?.destination.provider];
  const providerConnections = providerStatus?.connections || (providerStatus?.connection ? [providerStatus.connection] : []);
  const connectedProviderConnections = providerConnections.filter(item => item.status === 'connected');
  const destinationProviderIsConnected = connectedProviderConnections.length > 0;
  const soleConnectedConnectionId = connectedProviderConnections.length === 1
    ? connectedProviderConnections[0].id
    : '';
  const connection = draft?.destination.connectionId
    ? connectedProviderConnections.find(item => item.id === draft.destination.connectionId)
    : undefined;
  const showConnectionSelector = connectedProviderConnections.length > 1 || Boolean(
    destinationProviderIsConnected && connectedProviderConnections.length && !connection
  );
  const isAsana = draft?.destination.provider === 'asana';
  const destinationContext = draft?.destination.context || {};
  const selectedWorkspace = destinationOptions.workspaces.find(
    item => item.value === destinationContext.workspaceId
  );
  const workspaceRequiresTeam = Boolean(selectedWorkspace?.metadata?.isOrganization);
  const asanaContextReady = !isAsana || Boolean(
    destinationContext.workspaceId
    && (
      (draft?.destination.targetObject === 'tasks' && destinationContext.projectId)
      || (draft?.destination.targetObject === 'projects' && (!workspaceRequiresTeam || destinationContext.teamId))
    )
  );
  const sourceFields = useMemo(() => {
    const fields = [...(metadata?.sourceFields || []), ...(draft?.destination.sourceFields || [])];
    return [...new Map(fields.map(field => [field.apiName, field])).values()];
  }, [draft?.destination.sourceFields, metadata]);
  const targetFields = useMemo(() => (metadata?.fields || []).filter(field => field.createable), [metadata]);
  const requiredFields = useMemo(() => {
    const mapped = new Set((draft?.destination.fieldMappings || [])
      .filter(row => row.sourceField && row.targetField).map(row => row.targetField));
    const defaults = draft?.destination.defaults || {};
    return targetFields.filter(field => field.required && !field.defaultedOnCreate).map(field => ({
      ...field,
      covered: mapped.has(field.apiName) || String(defaults[field.apiName] ?? '').trim() !== '',
    }));
  }, [targetFields, draft?.destination.fieldMappings, draft?.destination.defaults]);
  const isBuiltinDraft = draft?.workflowType === 'builtin';
  useEffect(() => { onBusyChange?.(Boolean(dirty || loading || saveStatus === 'Saving…')); }, [dirty, loading, saveStatus, onBusyChange]);
  const selectedProviderStatusLoaded = Boolean(draft?.destination.provider && providerStatus);
  const selectedProviderNeedsConnection = Boolean(
    selectedProviderStatusLoaded && !destinationProviderIsConnected
  );
  const customWorkflowActivationBlocked = Boolean(
    draft?.workflowType === 'custom' && (
      !draft.destination.provider || connection?.status !== 'connected' || (isAsana && !asanaContextReady)
    )
  );
  const request = useCallback(async (action, callback) => {
    setLoading(action);
    setError('');
    try {
      return await callback();
    } catch (requestError) {
      logApiError({ page: 'Receptionist', operation: action, error: requestError });
      const apiError = requestError.response?.data?.error;
      const missing = apiError?.details?.missingFields;
      setError(missing?.length ? `${apiError.message} Missing: ${missing.join(', ')}.` : apiError?.message || 'Request failed.');
      return null;
    } finally {
      setLoading('');
    }
  }, []);

  const loadAllDestinationOptions = useCallback(async (kind, params) => {
    const options = [];
    let offset = '';
    do {
      const response = await axios.get(
        `${backendHost}/v2/integrations/asana/destination-options`,
        { ...authConfig, params: { ...params, kind, limit: 100, ...(offset ? { offset } : {}) } }
      );
      options.push(...(response.data.options || []));
      offset = response.data.nextOffset || '';
    } while (offset);
    return options;
  }, [authConfig, backendHost]);

  const loadWorkflows = useCallback(async () => {
    if (!jwtToken || !isAdmin) return;
    const response = await axios.get(`${backendHost}/v2/receptionist/workflows`, authConfig);
    const nextWorkflows = response.data.workflows || [];
    setWorkflows(nextWorkflows);
    setDraft(current => {
      if (!current?.id) return current;
      const refreshed = nextWorkflows.find(workflow => workflow.id === current.id);
      return refreshed ? toDraft(refreshed) : null;
    });
    setActiveCustomWorkflowLimit(response.data.activeCustomLimit ?? DEFAULT_ACTIVE_CUSTOM_WORKFLOW_LIMIT);
  }, [authConfig, backendHost, isAdmin, jwtToken]);

  useEffect(() => {
    if (!publishedVersion && !refreshVersion) return;
    loadWorkflows().catch(error => {
      logApiError({ page: 'Receptionist', operation: 'refreshPublishedWorkflows', error });
    });
  }, [loadWorkflows, publishedVersion, refreshVersion]);

  useEffect(() => {
    if (!archiveConfirmationOpen) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape' && loading !== 'archive') setArchiveConfirmationOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [archiveConfirmationOpen, loading]);

  useEffect(() => {
    if (!jwtToken || !isAdmin) return;
    request('initial', async () => {
      const [workflowResponse, providerResponse] = await Promise.all([
        axios.get(`${backendHost}/v2/receptionist/workflows`, authConfig),
        axios.get(`${backendHost}/v2/integrations/providers`, authConfig),
      ]);
      const nextProviders = providerResponse.data.providers || [];
      setWorkflows(workflowResponse.data.workflows || []);
      setActiveCustomWorkflowLimit(workflowResponse.data.activeCustomLimit ?? DEFAULT_ACTIVE_CUSTOM_WORKFLOW_LIMIT);
      setProviders(nextProviders);
      const results = await Promise.allSettled(nextProviders.map(provider => axios.get(
        `${backendHost}/v2/integrations/${provider.provider}/status`,
        authConfig
      )));
      results.forEach(result => {
        if (result.status === 'rejected') {
          logApiError({ page: 'Receptionist', operation: 'providerStatus', error: result.reason });
        }
      });
      setStatuses(Object.fromEntries(results.map((result, index) => [
        nextProviders[index].provider,
        result.status === 'fulfilled' ? result.value.data : { connection: null },
      ])));
    });
  }, [authConfig, backendHost, isAdmin, jwtToken, request]);

  useEffect(() => {
    if (draft || !workflows.length) return;
    const selectedBuiltin = workflows.find(workflow => (
      workflow.workflowType === 'builtin' && workflow.outcomeType === activeBuiltin
    ));
    if (!selectedBuiltin) return;
    setDraft(toDraft(selectedBuiltin));
    setDirty(false);
    setSaveStatus('Saved');
  }, [activeBuiltin, draft, workflows]);

  useEffect(() => {
    if (
      !draft?.id
      || draft.workflowType !== 'custom'
      || draft.destination.connectionId
      || !soleConnectedConnectionId
    ) return;
    editRevision.current += 1;
    setDraft(current => {
      if (!current || current.id !== draft.id || current.destination.connectionId) return current;
      return {
        ...current,
        hasUnpublishedChanges: true,
        destination: {
          ...current.destination,
          connectionId: soleConnectedConnectionId,
        },
      };
    });
    setDirty(true);
  }, [draft?.destination.connectionId, draft?.id, draft?.workflowType, soleConnectedConnectionId]);

  useEffect(() => {
    let active = true;
    setObjects([]);
    setObjectPermissionMessage('');
    if (!draft?.destination.provider || connection?.status !== 'connected') return undefined;
    request('objects', async () => {
      const response = await axios.get(
        `${backendHost}/v2/integrations/${draft.destination.provider}/objects`,
        { ...authConfig, params: { connection_id: draft.destination.connectionId } }
      );
      if (active) {
        setObjects((response.data.objects || []).filter(object => object.createable !== false));
        setObjectPermissionMessage(response.data.permissionMessage || '');
      }
    });
    return () => { active = false; };
  }, [authConfig, backendHost, connection?.status, draft?.destination.connectionId, draft?.destination.provider, request]);

  useEffect(() => {
    if (!isAsana || connection?.status !== 'connected') {
      setDestinationOptions({ workspaces: [], projects: [], teams: [], assignees: [] });
      return;
    }
    request('destinationOptions', async () => {
      const workspaces = await loadAllDestinationOptions('workspaces', {
        connection_id: draft.destination.connectionId,
      });
      setDestinationOptions(current => ({ ...current, workspaces }));
    });
  }, [connection?.status, draft?.destination.connectionId, isAsana, loadAllDestinationOptions, request]);

  useEffect(() => {
    if (!isAsana || connection?.status !== 'connected' || !destinationContext.workspaceId) {
      setDestinationOptions(current => ({ ...current, projects: [], teams: [], assignees: [] }));
      return;
    }
    request('destinationContext', async () => {
      const params = {
        connection_id: draft.destination.connectionId,
        workspace_id: destinationContext.workspaceId,
      };
      const [projects, teams, assignees] = await Promise.all([
        loadAllDestinationOptions('projects', params),
        loadAllDestinationOptions('teams', params),
        loadAllDestinationOptions('assignees', params),
      ]);
      setDestinationOptions(current => ({
        ...current,
        projects,
        teams,
        assignees,
      }));
    });
  }, [connection?.status, destinationContext.workspaceId, draft?.destination.connectionId, isAsana, loadAllDestinationOptions, request]);

  useEffect(() => {
    let active = true;
    setMetadata(null);
    setQuestionRecommendation(null);
    setMappingRecommendation(null);
    if (
      !draft?.destination.provider
      || !draft?.destination.targetObject
      || connection?.status !== 'connected'
      || !asanaContextReady
      || (draft.destination.provider === 'pipedrive' && !objects.some(object => object.apiName === draft.destination.targetObject))
    ) return undefined;
    request('metadata', async () => {
      const response = await axios.get(
        `${backendHost}/v2/integrations/${draft.destination.provider}/object/metadata`,
        { ...authConfig, params: {
          target_object: draft.destination.targetObject,
          connection_id: draft.destination.connectionId,
          workspace_id: destinationContext.workspaceId,
          project_id: destinationContext.projectId,
          team_id: destinationContext.teamId,
          assignee_id: destinationContext.assigneeId,
        } }
      );
      if (active) setMetadata(response.data);
    });
    return () => { active = false; };
  }, [asanaContextReady, authConfig, backendHost, connection?.status, destinationContext.assigneeId, destinationContext.projectId, destinationContext.teamId, destinationContext.workspaceId, draft?.destination.connectionId, draft?.destination.provider, draft?.destination.targetObject, objects, request]);

  const persistDraft = useCallback(async nextDraft => {
    if (!nextDraft?.id) return null;
    const savingRevision = editRevision.current;
    setSaveStatus('Saving…');
    try {
      const response = await axios.put(
        `${backendHost}/v2/receptionist/workflows/${nextDraft.id}`,
        workflowPayload(nextDraft),
        authConfig,
      );
      if (savingRevision === editRevision.current) {
        setSaveStatus('Saved');
        setDirty(false);
        setDraft(current => current?.id === nextDraft.id ? {
          ...current,
          hasUnpublishedChanges: Boolean(response.data.workflow.hasUnpublishedChanges),
        } : current);
      }
      setWorkflows(current => current.map(workflow => workflow.id === nextDraft.id ? response.data.workflow : workflow));
      if (response.data.workflow.hasUnpublishedChanges) onDraftStateChange?.();
      return response.data.workflow;
    } catch (saveError) {
      logApiError({ page: 'Receptionist', operation: 'saveWorkflow', error: saveError });
      if (savingRevision === editRevision.current) {
        setSaveStatus('Could not save');
        setDirty(true);
        const apiError = saveError.response?.data?.error;
        setError(apiError?.message || 'The workflow could not be saved.');
      }
      return null;
    }
  }, [authConfig, backendHost, onDraftStateChange]);

  useEffect(() => {
    if (!dirty || !draft?.id) return undefined;
    setSaveStatus('Unsaved changes');
    const timer = window.setTimeout(() => persistDraft(draft), 700);
    return () => window.clearTimeout(timer);
  }, [dirty, draft, persistDraft]);

  function updateDraft(updater) {
    editRevision.current += 1;
    setDraft(current => {
      const nextDraft = typeof updater === 'function' ? updater(current) : updater;
      return nextDraft?.workflowType === 'custom' ? { ...nextDraft, hasUnpublishedChanges: true } : nextDraft;
    });
    setDirty(true);
    setQuestionRecommendation(null);
    setMappingRecommendation(null);
  }

  function openCustomWorkflow(workflow) {
    editRevision.current += 1;
    setDraft(toDraft(workflow));
    setDirty(needsFollowUpClosingDraft(workflow));
    setSaveStatus(needsFollowUpClosingDraft(workflow) ? 'New closing needs publishing' : workflow.hasUnpublishedChanges ? 'Unpublished changes' : 'Saved');
    setQuestionRecommendation(null);
    setSelectedQuestionRecommendations([]);
    setMappingRecommendation(null);
    setSelectedMappingRecommendations([]);
    onCustomEditorChange(true);
  }

  function openBuiltinWorkflow(workflow) {
    editRevision.current += 1;
    setDraft(toDraft(workflow));
    setDirty(false);
    setSaveStatus('Saved');
    setQuestionRecommendation(null);
    setSelectedQuestionRecommendations([]);
    setMappingRecommendation(null);
    setSelectedMappingRecommendations([]);
    onCustomEditorChange(false);
    onSelectBuiltin(workflow.outcomeType);
  }

  async function selectWorkflow(workflow) {
    if (draft?.id === workflow.id) return;
    if (dirty) {
      const revision = editRevision.current;
      const saved = await persistDraft(draft);
      if (!saved || revision !== editRevision.current) return;
    }
    if (workflow.workflowType === 'builtin') openBuiltinWorkflow(workflow);
    else openCustomWorkflow(workflow);
  }

  async function createCustomWorkflow() {
    const result = await request('create', async () => {
      const response = await axios.post(
        `${backendHost}/v2/receptionist/workflows`,
        { name: `CRM workflow ${customWorkflows.length + 1}` },
        authConfig,
      );
      return response.data;
    });
    if (!result) return;
    await loadWorkflows();
    setLibrarySearch('');
    setLibraryFilter('all');
    setLibraryExpanded(true);
    openCustomWorkflow(result.workflow);
  }

  function chooseProvider(provider) {
    const providerState = statuses[provider];
    const connections = providerState?.connections || (providerState?.connection ? [providerState.connection] : []);
    const connectedConnections = connections.filter(item => item.status === 'connected');
    const providerConnection = connectedConnections.length === 1 ? connectedConnections[0] : null;
    const supportsAssignmentRules = providers.find(item => item.provider === provider)?.capabilities?.supportsAssignmentRules;
    updateDraft(current => ({
      ...current,
      destination: {
        ...current.destination,
        provider,
        connectionId: providerConnection?.id || '',
        targetObject: '',
        context: { workspaceId: '', projectId: '', teamId: '', assigneeId: '' },
        sourceFields: current.destination.sourceFields || [],
        fieldMappings: [{ ...EMPTY_MAPPING }],
        defaults: {},
        ownerPolicy: supportsAssignmentRules ? current.destination.ownerPolicy : 'default_crm_owner',
        mappingId: '',
        mappingVersion: null,
      },
    }));
  }

  function chooseConnection(connectionId) {
    updateDraft(current => ({
      ...current,
      destination: {
        ...current.destination,
        connectionId,
        targetObject: '',
        context: { workspaceId: '', projectId: '', teamId: '', assigneeId: '' },
        fieldMappings: [{ ...EMPTY_MAPPING }],
        defaults: {},
        mappingId: '',
        mappingVersion: null,
      },
    }));
  }

  function chooseObject(targetObject) {
    updateDraft(current => ({
      ...current,
      destination: {
        ...current.destination,
        targetObject,
        context: {
          ...(current.destination.context || {}),
          projectId: '',
          teamId: '',
          assigneeId: '',
        },
        sourceFields: current.destination.sourceFields || [],
        fieldMappings: [{ ...EMPTY_MAPPING }],
        defaults: {},
        mappingId: '',
        mappingVersion: null,
      },
    }));
  }

  function chooseDestinationContext(field, value) {
    updateDraft(current => {
      const context = { ...(current.destination.context || {}), [field]: value };
      const resetsMapping = field !== 'assigneeId';
      if (field === 'workspaceId') {
        context.projectId = '';
        context.teamId = '';
        context.assigneeId = '';
      }
      return {
        ...current,
        destination: {
          ...current.destination,
          targetObject: field === 'workspaceId' ? '' : current.destination.targetObject,
          context,
          fieldMappings: resetsMapping ? [{ ...EMPTY_MAPPING }] : current.destination.fieldMappings,
          defaults: resetsMapping ? {} : current.destination.defaults,
          mappingId: resetsMapping ? '' : current.destination.mappingId,
          mappingVersion: resetsMapping ? null : current.destination.mappingVersion,
        },
      };
    });
  }

  async function recommendQuestions() {
    if (!draft.intent.description.trim()) {
      setError('Describe when this workflow should be used before asking AI to recommend questions.');
      return;
    }
    const savedWorkflow = await persistDraft(draft);
    if (!savedWorkflow) return;
    const result = await request('recommendQuestions', async () => {
      const response = await axios.post(
        `${backendHost}/v2/integrations/${draft.destination.provider}/questions/recommend`,
        {
          connection_id: draft.destination.connectionId,
          targetObject: draft.destination.targetObject,
          context: destinationContext,
          workflowIntent: draft.intent,
          currentQuestions: draft.collection.steps,
          currentMapping: { ...draft.destination, collectionSteps: draft.collection.steps },
        },
        authConfig,
      );
      return response.data;
    });
    if (!result) return;
    setQuestionRecommendation(result);
    setSelectedQuestionRecommendations((result.questions || []).map(question => question.sourceField));
  }

  async function recommendIntent() {
    if (!draft.intent.description.trim()) {
      setError('Describe when this workflow should be used before asking AI for examples.');
      return;
    }
    const result = await request('recommendIntent', async () => {
      const response = await axios.post(
        `${backendHost}/v2/receptionist/workflows/intent/recommend`,
        {
          intent: {
            ...draft.intent,
            examples: draft.intent.examples.split('\n').map(item => item.trim()).filter(Boolean),
            exclusions: draft.intent.exclusions.split('\n').map(item => item.trim()).filter(Boolean),
          },
        },
        authConfig,
      );
      return response.data;
    });
    if (!result) return;
    updateDraft(current => ({
      ...current,
      intent: {
        ...current.intent,
        examples: (result.examples || []).join('\n'),
        clarificationPrompt: result.clarificationPrompt || current.intent.clarificationPrompt,
      },
    }));
  }

  function applyQuestionRecommendations() {
    const selectedQuestions = (questionRecommendation?.questions || [])
      .filter(question => selectedQuestionRecommendations.includes(question.sourceField));
    const selectedSources = new Set(selectedQuestions.map(question => question.sourceField));
    const nextSteps = selectedQuestions.map((question, index) => ({
        id: `${question.sourceField}:${index}`,
        sourceField: question.sourceField,
        label: question.label,
        spokenPrompt: question.spokenPrompt,
        reason: question.reason,
        required: question.required,
        confirm: question.confirm,
        origin: 'ai_recommended',
      }));
    const mergedSteps = [
      ...draft.collection.steps.filter(step => !selectedSources.has(step.sourceField)),
      ...nextSteps,
    ];
    const mergedSources = [...new Map([
      ...draft.destination.sourceFields,
      ...(questionRecommendation.sourceFields || []).filter(field => selectedSources.has(field.apiName)),
    ].map(field => [field.apiName, field])).values()];
    const nextDraft = {
      ...draft,
      collection: { steps: mergedSteps },
      destination: {
        ...draft.destination,
        sourceFields: mergedSources,
        mappingId: '',
        mappingVersion: null,
      },
    };
    editRevision.current += 1;
    setDraft(nextDraft);
    setQuestionRecommendation(null);
    setSelectedQuestionRecommendations([]);
    setMappingRecommendation(null);
    setSelectedMappingRecommendations([]);
    setDirty(true);
    persistDraft(nextDraft);
  }

  async function recommendMappings() {
    const savedWorkflow = await persistDraft(draft);
    if (!savedWorkflow) return;
    const result = await request('recommendMappings', async () => {
      const response = await axios.post(
        `${backendHost}/v2/integrations/${draft.destination.provider}/mappings/recommend`,
        {
          connection_id: draft.destination.connectionId,
          targetObject: draft.destination.targetObject,
          context: destinationContext,
          mode: draft.destination.fieldMappings.some(row => row.sourceField && row.targetField) ? 'improve' : 'recommend',
          workflowIntent: draft.intent,
          currentMapping: {
            ...draft.destination,
            sourceFields,
            collectionSteps: draft.collection.steps,
          },
        },
        authConfig,
      );
      return response.data;
    });
    if (!result) return;
    setMappingRecommendation(result);
    setSelectedMappingRecommendations([
      ...(result.fieldMappings || []).map(mapping => mapping.targetField),
      ...Object.keys(result.defaults || {}).map(targetField => `default:${targetField}`),
    ]);
  }

  function applyMappingRecommendations() {
    const selectedMappings = (mappingRecommendation?.fieldMappings || [])
      .filter(mapping => selectedMappingRecommendations.includes(mapping.targetField));
    const selectedDefaults = Object.fromEntries(Object.entries(mappingRecommendation?.defaults || {})
      .filter(([targetField]) => selectedMappingRecommendations.includes(`default:${targetField}`)));
    const selectedTargets = new Set(selectedMappings.map(mapping => mapping.targetField));
    const existingMappings = draft.destination.fieldMappings.filter(mapping => mapping.sourceField && mapping.targetField);
    const mergedMappings = [
      ...existingMappings.filter(mapping => !selectedTargets.has(mapping.targetField)),
      ...selectedMappings,
    ];
    const nextDefaults = Object.fromEntries(Object.entries(draft.destination.defaults)
      .filter(([targetField]) => !selectedTargets.has(targetField)));
    const nextDraft = {
      ...draft,
      destination: {
        ...draft.destination,
        fieldMappings: mergedMappings.length ? mergedMappings : [{ ...EMPTY_MAPPING }],
        defaults: { ...nextDefaults, ...selectedDefaults },
        mappingId: '',
        mappingVersion: null,
      },
    };
    editRevision.current += 1;
    setDraft(nextDraft);
    setMappingRecommendation(null);
    setSelectedMappingRecommendations([]);
    setDirty(true);
    persistDraft(nextDraft);
  }

  function updateMapping(index, key, value) {
    updateDraft(current => ({
      ...current,
      destination: {
        ...current.destination,
        mappingId: '',
        mappingVersion: null,
        fieldMappings: current.destination.fieldMappings.map((row, rowIndex) => {
          if (rowIndex !== index) return row;
          if (key === 'sourceField') return { ...row, sourceField: value, sourceFields: value ? [value] : [], transform: { type: 'direct' } };
          return { ...row, [key]: value };
        }),
      },
    }));
  }

  function addCollectionStep() {
    if (isBuiltinDraft) {
      const sourceField = `${draft.outcomeType}.custom_${Date.now()}`;
      updateDraft(current => ({
        ...current,
        collection: {
          steps: [...current.collection.steps, {
            id: sourceField,
            sourceField,
            label: 'New information',
            spokenPrompt: '',
            reason: '',
            required: true,
            confirm: false,
            origin: 'user',
          }],
        },
      }));
      return;
    }
    const used = new Set(draft.collection.steps.map(step => step.sourceField));
    const available = sourceFields.find(field => !used.has(field.apiName) && !['callSessionId', 'notes'].includes(field.apiName));
    if (!available) {
      setError('Add or select another mapped source field before adding a question.');
      return;
    }
    updateDraft(current => ({
      ...current,
      collection: {
        steps: [...current.collection.steps, {
          id: `${available.apiName}:${Date.now()}`,
          sourceField: available.apiName,
          label: available.label,
          spokenPrompt: '',
          reason: '',
          required: true,
          confirm: false,
          origin: 'user',
        }],
      },
    }));
  }

  async function activate() {
    if (dirty) {
      const savedWorkflow = await persistDraft(draft);
      if (!savedWorkflow) return;
    }
    const result = await request('activate', async () => {
      const response = await axios.post(
        `${backendHost}/v2/receptionist/workflows/${draft.id}/activate`,
        {},
        authConfig,
      );
      return response.data;
    });
    if (!result) return;
    setDraft(toDraft(result.workflow));
    setSaveStatus('Ready to publish');
    setWorkflows(current => current.map(workflow => workflow.id === draft.id ? result.workflow : workflow));
    await onDraftStateChange?.();
  }

  async function pause() {
    const result = await request('pause', async () => {
      const response = await axios.post(
        `${backendHost}/v2/receptionist/workflows/${draft.id}/pause`,
        {},
        authConfig,
      );
      return response.data;
    });
    if (!result) return;
    setDraft(toDraft(result.workflow));
    setSaveStatus('Ready to publish');
    setWorkflows(current => current.map(workflow => workflow.id === draft.id ? result.workflow : workflow));
    await onDraftStateChange?.();
  }

  async function toggleWorkflowStatus() {
    if (draft.status === 'active') {
      if (dirty) {
        const savedWorkflow = await persistDraft(draft);
        if (!savedWorkflow) return;
      }
      pause();
      return;
    }
    activate();
  }

  async function archive() {
    const result = await request('archive', async () => {
      const response = await axios.delete(
        `${backendHost}/v2/receptionist/workflows/${draft.id}`,
        authConfig
      );
      return response.data;
    });
    if (!result) return;
    setArchiveConfirmationOpen(false);
    await onDraftStateChange?.();
    editRevision.current += 1;
    setDraft(null);
    onCustomEditorChange(false);
    await loadWorkflows();
  }

  return (
    <section id="workflows" className="workflow-manager" aria-labelledby="workflow-manager-title">
      <div className="workflow-manager-heading">
        <div><span className="workflow-kicker">Receptionist services</span><h2 id="workflow-manager-title">What should happen when someone calls?</h2><p>Each workflow defines an intent, the information Sayable.ai collects, and the outcome it completes.</p></div>
        {isAdmin && (!setupGoal || setupGoal === 'crm') && activeCustomWorkflowLimit > 0 ? <div className="workflow-manager-actions"><small title="Includes workflows enabled in the draft">{activeCustomWorkflowCount} of {activeCustomWorkflowLimit} enabled custom workflows</small><button className="workflow-add-button" onClick={createCustomWorkflow} disabled={loading === 'create'} aria-busy={loading === 'create' || undefined}><BusyContent busy={loading === 'create'} label={"Creating…"}>{<><span className="workflow-add-icon" aria-hidden="true">+</span>Add CRM workflow</>}</BusyContent></button></div> : null}
      </div>

      {error ? <div className="workflow-alert" role="alert">{error}<button onClick={() => setError('')} aria-label="Dismiss">×</button></div> : null}
      {!isAdmin ? <div className="workflow-alert">Only organization admins can manage receptionist workflows.</div> : null}
      {isAdmin ? <WorkflowAttention backendHost={backendHost} authConfig={authConfig} workflows={workflows} /> : null}

      <div className="workflow-library">
      {isAdmin ? <div className="workflow-library-toolbar">
        <div className="workflow-library-search">
          <input ref={searchRef} type="search" aria-label="Search workflows" placeholder="Search workflows…"
            value={librarySearch} onChange={event => setLibrarySearch(event.target.value)} aria-controls="workflow-library-grid" />
          {librarySearch ? <button type="button" aria-label="Clear workflow search" onClick={() => {
            setLibrarySearch('');
            searchRef.current?.focus();
          }}>×</button> : null}
        </div>
        <div className="workflow-library-filters" role="group" aria-label="Filter workflows">
          <button type="button" aria-pressed={libraryFilter === 'all'} aria-controls="workflow-library-grid"
            onClick={() => { setLibraryFilter('all'); setLibraryExpanded(false); }}>All ({workflows.length})</button>
          <button type="button" aria-pressed={libraryFilter === 'enabled'} aria-controls="workflow-library-grid"
            onClick={() => { setLibraryFilter('enabled'); setLibraryExpanded(false); }}>Enabled ({library.enabledCount})</button>
        </div>
      </div> : null}
      {isAdmin && workflows.length > 0 ? <p className="workflow-library-count" role="status">
        Showing {library.visible.length} of {library.matches.length} {librarySearch.trim() ? 'matching ' : ''}workflows
      </p> : null}

      <div id="workflow-library-grid" className="workflow-grid">
        {library.visible.map(workflow => {
          const meta = BUILTIN_META[workflow.outcomeType] || BUILTIN_META.message;
          const builtin = workflow.workflowType === 'builtin';
          const selected = draft?.id === workflow.id || (activeBuiltin === workflow.outcomeType && !draft);
          const displayStatus = workflow.publishedStatus || 'draft';
          const pending = workflow.hasUnpublishedChanges || (draft?.id === workflow.id && dirty);
          return (
            <button type="button" className={`workflow-card ${selected ? 'selected' : ''}`} key={workflow.id}
              aria-pressed={selected} onClick={() => selectWorkflow(workflow)}>
              {builtin ? <BuiltinIcon type={workflow.outcomeType} color={meta.color} /> : <ProviderBadge provider={workflow.destination?.provider} />}
              <span className="workflow-card-copy"><span className="workflow-card-label">{builtin ? 'Built in' : 'Custom CRM'}</span><strong>{workflow.name}</strong><small>{workflow.intent?.description || 'Describe when callers should enter this workflow.'}</small></span>
              <span className={`workflow-status ${displayStatus}`}>{workflowStatusLabel(displayStatus)}</span>
              <span className="workflow-card-action">{builtin ? meta.editLabel : 'Configure workflow'} →</span>
              {pending ? <span className="workflow-card-pending">Unpublished changes</span> : null}
            </button>
          );
        })}
        {!workflows.length && loading === 'initial' ? <div className="workflow-loading" role="status"><BusyLabel>Loading receptionist workflows…</BusyLabel></div> : null}
        {isAdmin && loading !== 'initial' && !library.matches.length ? <div className="workflow-library-empty">
          <p>{librarySearch.trim() ? 'No matching workflows.' : libraryFilter === 'enabled' ? 'No workflows are enabled yet.' : 'No workflows yet.'}</p>
          {librarySearch.trim() ? <Button variant="secondary" size="sm" onClick={() => {
            setLibrarySearch(''); searchRef.current?.focus();
          }}>Clear search</Button> : null}
          {libraryFilter === 'enabled' ? <Button variant="secondary" size="sm" onClick={() => {
            setLibraryFilter('all'); setLibraryExpanded(false);
          }}>View all workflows</Button> : null}
        </div> : null}
      </div>
      {!librarySearch.trim() && library.matches.length > WORKFLOW_PREVIEW_COUNT ? <div className="workflow-library-expand">
        <Button variant="secondary" size="sm" aria-expanded={libraryExpanded} aria-controls="workflow-library-grid"
          onClick={() => setLibraryExpanded(value => !value)}>{libraryExpanded ? 'Show fewer' : `Show all ${library.matches.length}`}</Button>
      </div> : null}
      </div>

      {isBuiltinDraft ? (
        <div className="workflow-editor workflow-builtin-editor" tabIndex={-1} role="region" aria-label={`Configure ${draft.name}`}>
          <div className="workflow-editor-header">
            <div><span className="workflow-kicker">Built-in service</span><h3 className="workflow-builtin-title">{draft.name}</h3><p>Choose only the information a receptionist can naturally collect over the phone before completing this service.</p></div>
            <div className="workflow-editor-state"><WorkflowStatusToggle status={draft.status} publishedStatus={draft.publishedStatus} hasUnpublishedChanges={draft.hasUnpublishedChanges} busy={loading === 'activate' || loading === 'pause'} onChange={toggleWorkflowStatus} /><small className={saveStatus === 'Saving…' ? 'workflow-save-status busy' : 'workflow-save-status'}>{saveStatus === 'Saving…' ? <BusyLabel>{saveStatus}</BusyLabel> : saveStatus}</small></div>
          </div>
          <div className="workflow-editor-section">
            <div className="workflow-section-number">1</div>
            <div className="workflow-section-body">
              <h3>What should the receptionist collect?</h3>
              <p>Questions run from top to bottom. Standard service actions, such as taking the message, booking the appointment, or routing the call, remain automatic.</p>
              <CollectionEditor
                steps={draft.collection.steps}
                sourceFields={[]}
                freeformLabels
                onChange={steps => updateDraft(current => ({ ...current, collection: { steps } }))}
                onAdd={addCollectionStep}
              />
            </div>
          </div>
        </div>
      ) : draft ? (
        <div className="workflow-editor" tabIndex={-1} role="region" aria-label={`Configure ${draft.name}`}>
          <div className="workflow-editor-header">
            <div><span className="workflow-kicker">Custom CRM workflow</span><input className="workflow-name-input" value={draft.name} onChange={event => updateDraft(current => ({ ...current, name: event.target.value }))} aria-label="Workflow name" /><p>Draft changes save automatically. Publish when this path is ready for live callers.</p></div>
            <div className="workflow-editor-state"><WorkflowStatusToggle status={draft.status} publishedStatus={draft.publishedStatus} hasUnpublishedChanges={draft.hasUnpublishedChanges} busy={loading === 'activate' || loading === 'pause'} disabled={draft.status !== 'active' && (customWorkflowActivationBlocked || activeCustomWorkflowLimitReached)} inactiveMessage={activeCustomWorkflowLimitReached ? `${activeCustomWorkflowLimit} enabled workflow limit reached` : customWorkflowActivationBlocked ? (draft.destination.provider ? 'Connect CRM to publish' : 'Choose a CRM to publish') : 'Ready to publish'} onChange={toggleWorkflowStatus} /><small className="workflow-published-at">{lastPublishedLabel(draft.activatedAt)}</small><small className={saveStatus === 'Saving…' ? 'workflow-save-status busy' : 'workflow-save-status'}>{saveStatus === 'Saving…' ? <BusyLabel>{saveStatus}</BusyLabel> : saveStatus}</small></div>
          </div>

          <div className="workflow-editor-section">
            <div className="workflow-section-number">1</div><div className="workflow-section-body"><h3>When should Sayable use this?</h3><p>Describe the caller’s goal in plain language. AI uses the full conversation, not exact keyword rules, to select the path.</p>
              <label>Intent description<textarea value={draft.intent.description} onChange={event => updateDraft(current => ({ ...current, intent: { ...current.intent, description: event.target.value } }))} placeholder="Example: Use when a new customer wants an estimate for a service." /></label>
              <div className="workflow-intent-ai-action"><span>Enter the intent first. AI will use it to write realistic caller examples and a clarifying question.</span><button type="button" className="workflow-ai-button workflow-intent-ai-button" onClick={recommendIntent} disabled={loading === 'recommendIntent' || !draft.intent.description.trim()} aria-busy={loading === 'recommendIntent' || undefined} title={!draft.intent.description.trim() ? 'Enter an intent description first.' : 'Generate from the intent description'}><BusyContent busy={loading === 'recommendIntent'} label={"Generating…"}>{'✦ Generate examples and question'}</BusyContent></button></div>
              <div className="workflow-two-column workflow-intent-boundaries"><label>Example caller requests<textarea value={draft.intent.examples} onChange={event => updateDraft(current => ({ ...current, intent: { ...current.intent, examples: event.target.value } }))} placeholder={'I need an estimate\nCan someone quote this project?'} /></label><label>Do not use when (optional)<textarea value={draft.intent.exclusions} onChange={event => updateDraft(current => ({ ...current, intent: { ...current.intent, exclusions: event.target.value } }))} placeholder={'Existing customer support\nAppointment changes'} /></label></div>
              <label>Clarifying question <input value={draft.intent.clarificationPrompt} onChange={event => updateDraft(current => ({ ...current, intent: { ...current.intent, clarificationPrompt: event.target.value } }))} placeholder="Are you looking for a new estimate or help with existing service?" /></label>
            </div>
          </div>

          <div className="workflow-editor-section">
            <div className="workflow-section-number">2</div><div className="workflow-section-body"><h3>Where should the information go?</h3><p>Choose any supported CRM. It must be connected before you can select an account, record type, or publish the workflow.</p>
              <div className="workflow-two-column">
                <label>CRM<select value={draft.destination.provider} onChange={event => chooseProvider(event.target.value)}><option value="">Select CRM</option>{providers.map(provider => { const status = statuses[provider.provider]; const connections = status?.connections || (status?.connection ? [status.connection] : []); const connected = connections.some(item => item.status === 'connected'); const connectionLabel = status ? (connected ? 'Connected' : 'Not connected') : ''; return <option value={provider.provider} key={provider.provider}>{provider.displayName}{connectionLabel ? ` · ${connectionLabel}` : ''}</option>; })}</select></label>
                {showConnectionSelector ? <label>Account<select value={connection?.id || ''} onChange={event => chooseConnection(event.target.value)}><option value="">Select CRM account</option>{connectedProviderConnections.map(item => <option value={item.id} key={item.id}>{item.crm_org_name || item.crm_org_id || item.crm_user_id || item.id}</option>)}</select></label> : isAsana ? <label>Workspace<select value={destinationContext.workspaceId || ''} onChange={event => chooseDestinationContext('workspaceId', event.target.value)} disabled={connection?.status !== 'connected'}><option value="">Select workspace</option>{destinationOptions.workspaces.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label> : <label>Record type<select value={draft.destination.targetObject} onChange={event => chooseObject(event.target.value)} disabled={!draft.destination.provider || connection?.status !== 'connected'}><option value="">Select record type</option>{objects.map(object => <option value={object.apiName} key={object.apiName}>{object.label}</option>)}</select></label>}
              </div>
              {showConnectionSelector && isAsana ? <label>Workspace<select value={destinationContext.workspaceId || ''} onChange={event => chooseDestinationContext('workspaceId', event.target.value)} disabled={!connection}><option value="">Select workspace</option>{destinationOptions.workspaces.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label> : null}
              {showConnectionSelector && !isAsana ? <label>Record type<select value={draft.destination.targetObject} onChange={event => chooseObject(event.target.value)} disabled={!connection}><option value="">Select record type</option>{objects.map(object => <option value={object.apiName} key={object.apiName}>{object.label}</option>)}</select></label> : null}
              {isAsana && destinationContext.workspaceId ? <div className="workflow-two-column">
                <label>Record type<select value={draft.destination.targetObject} onChange={event => chooseObject(event.target.value)}><option value="">Select record type</option>{objects.map(object => <option value={object.apiName} key={object.apiName}>{object.label}</option>)}</select></label>
                {draft.destination.targetObject === 'tasks' ? <label>Project<select value={destinationContext.projectId || ''} onChange={event => chooseDestinationContext('projectId', event.target.value)}><option value="">Select project</option>{destinationOptions.projects.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label> : null}
                {draft.destination.targetObject === 'projects' && workspaceRequiresTeam ? <label>Team<select value={destinationContext.teamId || ''} onChange={event => chooseDestinationContext('teamId', event.target.value)}><option value="">Select team</option>{destinationOptions.teams.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label> : null}
              </div> : null}
              {isAsana && draft.destination.targetObject === 'tasks' && destinationContext.projectId ? <label>Assignee (optional)<select value={destinationContext.assigneeId || ''} onChange={event => chooseDestinationContext('assigneeId', event.target.value)}><option value="">Leave unassigned</option>{destinationOptions.assignees.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label> : null}
              {metadata?.relatedContact?.message ? <p className="workflow-inline-help" role="status">{metadata.relatedContact.message}</p> : null}
              {objectPermissionMessage ? <div className="workflow-connection-guidance" role="status">{objectPermissionMessage}</div> : null}
              {loading === 'objects' ? <div className="workflow-inline-loading" role="status"><BusyLabel>Loading record types…</BusyLabel></div> : null}
              {loading === 'destinationOptions' || loading === 'destinationContext' ? <div className="workflow-inline-loading" role="status"><BusyLabel>Loading Asana choices…</BusyLabel></div> : null}
              {loading === 'metadata' ? <div className="workflow-inline-loading" role="status"><BusyLabel>Loading CRM fields…</BusyLabel></div> : null}
              <p className="workflow-inline-help">{selectedProvider?.capabilities?.alwaysCreatesRecord ? `Sayable creates a new ${draft.destination.targetObject === 'projects' ? 'Asana project' : 'Asana task'} for every completed workflow.` : 'Sayable updates the first matching CRM record automatically, or creates one when no match is found.'}</p>
              {selectedProviderNeedsConnection ? <div className="workflow-connection-guidance" role="status"><div><strong>Connect {selectedProvider?.displayName || draft.destination.provider} to continue</strong><span>Custom workflows can use this CRM after it is connected to your organization. Once connected, return here to choose the account and record type.</span></div><Link className="workflow-connect-button" to={`/integrations?provider=${encodeURIComponent(draft.destination.provider)}`}>Connect {selectedProvider?.displayName || draft.destination.provider} →</Link></div> : null}
            </div>
          </div>

          {metadata ? (
            <>
              {requiredFields.length ? <div className="workflow-required-fields" role="status"><strong>Required for {selectedProvider?.displayName || draft.destination.provider} {metadata.object?.label || draft.destination.targetObject}</strong><p>To create this record, collect each missing detail from the caller, map an answer you already collect, or set a fixed value below.</p><ul>{requiredFields.map(field => <li key={field.apiName} className={field.covered ? 'covered' : ''}><span>{field.label} ({field.apiName})</span><span>{field.covered ? 'Ready' : 'Choose how to fill this'}</span></li>)}</ul></div> : null}
              <div className="workflow-editor-section">
                <div className="workflow-section-number">3</div><div className="workflow-section-body"><div className="workflow-section-heading"><div><h3>What should the receptionist collect?</h3><p>These conversational questions run from top to bottom. Keep the intake short and natural for a phone call.</p></div><button className="workflow-ai-button" onClick={recommendQuestions} disabled={loading === 'recommendQuestions' || !draft.intent.description.trim()} aria-busy={loading === 'recommendQuestions' || undefined} title={!draft.intent.description.trim() ? 'Enter an intent description in Step 1 first.' : 'Recommend questions for this workflow'}><BusyContent busy={loading === 'recommendQuestions'} label={"Asking AI…"}>{'✦ Recommend questions'}</BusyContent></button></div>
                  {!draft.intent.description.trim() ? <p className="workflow-inline-help">Enter an intent description in Step 1 to enable AI question recommendations.</p> : null}
                  {questionRecommendation ? <div className="workflow-recommendation"><div className="workflow-recommendation-heading"><div><strong>AI-recommended questions</strong><p>{questionRecommendation.summary}</p></div><span>{selectedQuestionRecommendations.length} selected</span></div>{(questionRecommendation.questions || []).map(question => <label className="workflow-recommendation-row" key={question.sourceField}><input type="checkbox" checked={selectedQuestionRecommendations.includes(question.sourceField)} onChange={() => setSelectedQuestionRecommendations(current => current.includes(question.sourceField) ? current.filter(item => item !== question.sourceField) : [...current, question.sourceField])} /><span className="workflow-recommendation-copy"><strong>{question.label}</strong>{question.requiredTargetFields?.length ? <small>Can provide: {question.requiredTargetFields.map(name => targetFields.find(field => field.apiName === name)?.label || name).join(', ')}. Map the answer in Step 4.</small> : null}<small className="workflow-recommendation-prompt"><b>Ask</b><span>“{question.spokenPrompt}”</span></small><small className="workflow-recommendation-reason">{question.reason}</small></span>{question.reviewRequired ? <em>Review</em> : null}</label>)}{questionRecommendation.unresolvedRequiredFields?.length ? <p className="workflow-inline-help">These required fields still need attention: {questionRecommendation.unresolvedRequiredFields.map(field => field.label).join(', ')}. Add a question if a caller can provide the answer, or resolve the field in Step 4.</p> : null}<div className="workflow-recommendation-actions"><button className="workflow-primary" onClick={applyQuestionRecommendations} disabled={!selectedQuestionRecommendations.length}>Apply selected questions</button><button className="workflow-link-button" onClick={() => setQuestionRecommendation(null)}>Dismiss</button></div></div> : null}
                  <CollectionEditor
                    steps={draft.collection.steps}
                    sourceFields={sourceFields}
                    onChange={steps => updateDraft(current => ({ ...current, collection: { steps } }))}
                    onAdd={addCollectionStep}
                  />
                </div>
              </div>

              <div className="workflow-editor-section">
                <div className="workflow-section-number">4</div><div className="workflow-section-body"><div className="workflow-section-heading"><div><h3>How should collected values map?</h3><p>The saved intake values become fields on the selected CRM record.</p></div><button className="workflow-ai-button" onClick={recommendMappings} disabled={loading === 'recommendMappings'} aria-busy={loading === 'recommendMappings' || undefined}><BusyContent busy={loading === 'recommendMappings'} label={"Asking AI…"}>{'✦ Recommend mappings'}</BusyContent></button></div>
                  {mappingRecommendation ? <div className="workflow-recommendation"><div className="workflow-recommendation-heading"><div><strong>AI-recommended mappings</strong><p>{mappingRecommendation.summary}</p></div><span>{selectedMappingRecommendations.length} selected</span></div>{(mappingRecommendation.fieldMappings || []).map(mapping => { const target = targetFields.find(field => field.apiName === mapping.targetField); return <label className="workflow-recommendation-row" key={mapping.targetField}><input type="checkbox" checked={selectedMappingRecommendations.includes(mapping.targetField)} onChange={() => setSelectedMappingRecommendations(current => current.includes(mapping.targetField) ? current.filter(item => item !== mapping.targetField) : [...current, mapping.targetField])} /><span><strong>{mappingSourceLabel(mapping, sourceFields)} → {target?.label || mapping.targetField}</strong><small>{mapping.reason}</small></span>{mapping.reviewRequired ? <em>Review</em> : null}</label>; })}{Object.entries(mappingRecommendation.defaults || {}).map(([targetField, value]) => { const selectionKey = `default:${targetField}`; const target = targetFields.find(field => field.apiName === targetField); return <label className="workflow-recommendation-row" key={selectionKey}><input type="checkbox" checked={selectedMappingRecommendations.includes(selectionKey)} onChange={() => setSelectedMappingRecommendations(current => current.includes(selectionKey) ? current.filter(item => item !== selectionKey) : [...current, selectionKey])} /><span><strong>Default “{String(value)}” → {target?.label || targetField}</strong><small>Used only when this workflow does not collect a value for the CRM field.</small></span><em>Review</em></label>; })}<div className="workflow-recommendation-actions"><button className="workflow-primary" onClick={applyMappingRecommendations} disabled={!selectedMappingRecommendations.length}>Apply selected mappings</button><button className="workflow-link-button" onClick={() => setMappingRecommendation(null)}>Dismiss</button></div></div> : null}
                  <div className="workflow-mapping-list">{draft.destination.fieldMappings.map((mapping, index) => <div className="workflow-mapping-row" key={`${mapping.targetField}-${index}`}><select value={mapping.sourceField} onChange={event => updateMapping(index, 'sourceField', event.target.value)}><option value="">Select collected value</option>{sourceFields.map(field => <option value={field.apiName} key={field.apiName}>{field.label}</option>)}</select><span>→</span><select value={mapping.targetField} onChange={event => updateMapping(index, 'targetField', event.target.value)}><option value="">Select CRM field</option>{targetFields.map(field => <option value={field.apiName} key={field.apiName}>{field.label} ({field.apiName}){field.required ? ' · Required' : ''}</option>)}</select><button className="workflow-remove" onClick={() => updateDraft(current => ({ ...current, destination: { ...current.destination, fieldMappings: current.destination.fieldMappings.filter((_, itemIndex) => itemIndex !== index) } }))} disabled={draft.destination.fieldMappings.length === 1}>×</button></div>)}</div>
                  <button className="workflow-link-button" onClick={() => updateDraft(current => ({ ...current, destination: { ...current.destination, fieldMappings: [...current.destination.fieldMappings, { ...EMPTY_MAPPING }] } }))}>+ Add mapping</button>
                  {requiredFields.filter(field => !field.covered && !Object.prototype.hasOwnProperty.call(draft.destination.defaults, field.apiName)).map(field => <button className="workflow-link-button" key={field.apiName} onClick={() => updateDraft(current => ({ ...current, destination: { ...current.destination, defaults: { ...current.destination.defaults, [field.apiName]: '' } } }))}>+ Set reviewed fallback for {field.label}</button>)}
                  {draft.destination.provider === 'pipedrive' ? <>
                    {(draft.destination.fieldMappings.some(row => row.targetField?.startsWith('contact.')) || Object.keys(draft.destination.defaults).some(name => name.startsWith('contact.')) ? [] : metadata.requiredAnyOf || []).map(group => <p className="workflow-inline-help" key={group.join(',')} role="status">Provide at least one: {group.map(name => targetFields.find(field => field.apiName === name)?.label || name).join(' or ')}.</p>)}
                  </> : null}
                  {draft.destination.provider !== 'pipedrive' && Object.keys(draft.destination.defaults).length ? <div className="workflow-default-list"><strong>Reviewed fallback values</strong>{Object.entries(draft.destination.defaults).map(([targetField, value]) => {
                    const field = targetFields.find(item => item.apiName === targetField);
                    const label = field?.label || targetField;
                    const change = event => updateDraft(current => ({ ...current, destination: { ...current.destination, defaults: { ...current.destination.defaults, [targetField]: event.target.value } } }));
                    return <div key={targetField}><span>{label}</span>{field?.type === 'picklist' && field.picklistValues?.length ? <select aria-label={`${label} fallback`} value={value} onChange={change}><option value="">Select value</option>{field.picklistValues.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select> : <input aria-label={`${label} fallback`} value={value} onChange={change} />}<button className="workflow-remove" onClick={() => updateDraft(current => { const defaults = { ...current.destination.defaults }; delete defaults[targetField]; return { ...current, destination: { ...current.destination, defaults } }; })} aria-label="Remove fallback">×</button></div>;
                  })}</div> : null}
                </div>
              </div>
            </>
          ) : null}

          <div className="workflow-editor-section">
            <div className="workflow-section-number">{metadata ? '5' : '3'}</div><div className="workflow-section-body"><h3>What should the caller hear?</h3><p>Set a natural introduction and closing for this path. Changes save as a draft and need to be published before callers hear them.</p>
              <label>Opening (optional)<textarea value={draft.behavior.leadIn} maxLength={180} onChange={event => updateDraft(current => ({ ...current, behavior: { ...current.behavior, leadIn: event.target.value } }))} placeholder="I can take a few details about your request and pass them along to the team." /></label>
              <p className="workflow-inline-help">If left blank, the receptionist introduces the selected path naturally. Keep this to one short sentence before the first intake question.</p>
              <label>Closing guidance<textarea value={draft.behavior.successMessage} maxLength={500} onChange={event => updateDraft(current => ({ ...current, behavior: { ...current.behavior, successMessage: event.target.value } }))} /></label>
              <p className="workflow-inline-help">The receptionist will say this naturally after the intake, then end the call. Only promise follow-up that your team can deliver.</p>
            </div>
          </div>

          <div className="workflow-editor-footer"><button className="workflow-danger-link" onClick={() => setArchiveConfirmationOpen(true)} disabled={loading === 'archive'}>Archive workflow</button><div><span className="workflow-footer-status">{dirty || saveStatus === 'Saving…' ? <LoadingIndicator label="Saving draft…" /> : draft.hasUnpublishedChanges ? 'Use Review and publish above when these changes are ready.' : saveStatus}</span></div></div>
        </div>
      ) : null}
      {archiveConfirmationOpen && draft ? (
        <div className="workflow-archive-modal-backdrop" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget && loading !== 'archive') setArchiveConfirmationOpen(false);
        }}>
          <section className="workflow-archive-modal" role="alertdialog" aria-modal="true" aria-labelledby="workflow-archive-title" aria-describedby="workflow-archive-description">
            <div className="workflow-archive-modal-heading">
              <div><span className="say-eyebrow">Archive workflow</span><h2 id="workflow-archive-title">Archive “{draft.name}”?</h2></div>
              <button type="button" aria-label="Close archive confirmation" onClick={() => setArchiveConfirmationOpen(false)} disabled={loading === 'archive'}>×</button>
            </div>
            <p id="workflow-archive-description">This workflow will no longer be available to callers or appear in your workflow library.</p>
            <div className="workflow-archive-note"><span aria-hidden="true">!</span><p>CRM records already created or updated by this workflow will not be changed.</p></div>
            <div className="workflow-archive-modal-actions">
              <Button variant="secondary" autoFocus onClick={() => setArchiveConfirmationOpen(false)} disabled={loading === 'archive'}>Cancel</Button>
              <Button variant="danger" loading={loading === 'archive'} loadingLabel="Archiving…" onClick={archive}>Archive workflow</Button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

export default WorkflowManager;
